import React, {useState, useEffect, useRef} from 'react'
import {Box} from '@chakra-ui/react'
import {useInView} from '../../../hooks/use-in-view'

/**
 * Higher-Order Component for viewport-based hydration deferral
 *
 * SSR: Renders full content for SEO (search engines see complete HTML)
 * Client: Renders full content initially to match SSR, then controls visibility based on viewport
 *
 * This reduces main thread work by delaying JS execution for below-fold components
 *
 * For components that use useCarouselBleed or similar hooks that need DOM measurements,
 * this HOC passes a `hydrationCount` prop that increments when hydration occurs.
 * Use this as `remeasureTrigger` in useCarouselBleed to force re-measurement after hydration.
 *
 * @param {React.Component} Component - Component to wrap
 * @param {Object} options - Configuration options
 * @param {string} options.rootMargin - IntersectionObserver rootMargin (default: '200px')
 * @param {number} options.minHeight - Minimum height for placeholder (default: 200)
 */
export const withViewportHydration = (Component, options = {}) => {
    const {rootMargin = '200px', minHeight = 200} = options

    const HydratedComponent = (props) => {
        const {ref, hasBeenInView} = useInView({
            rootMargin,
            triggerOnce: true,
            initialInView: false
        })

        // Track if initial hydration has completed
        const hasHydrated = useRef(false)

        // Always render on first render to match SSR output and avoid hydration mismatch
        // After hydration, we can control rendering based on viewport
        const [shouldRender, setShouldRender] = useState(true)
        // Counter that increments each time hydration occurs - used to trigger re-measurements
        const [hydrationCount, setHydrationCount] = useState(0)

        useEffect(() => {
            // Mark that hydration is complete
            hasHydrated.current = true

            // If component was not in view at mount time, we could hide it
            // But for simplicity and to avoid content flash, we keep it rendered
            // The performance benefit comes from deferring heavy JS initialization

            if (hasBeenInView) {
                // Increment hydration count to signal components to re-measure
                // Use setTimeout to ensure DOM is updated before measurement
                setTimeout(() => {
                    setHydrationCount((c) => c + 1)
                }, 0)
            }
        }, [hasBeenInView])

        return (
            <Box ref={ref} minHeight={!shouldRender ? `${minHeight}px` : undefined}>
                {shouldRender ? <Component {...props} hydrationCount={hydrationCount} /> : null}
            </Box>
        )
    }

    HydratedComponent.displayName = `withViewportHydration(${
        Component.displayName || Component.name || 'Component'
    })`
    return HydratedComponent
}

export default withViewportHydration
